import type { WorkbookInternals } from './workbookInternals';
import {
    buildEscState,
    resolveEscape,
    closeMenusRecipe,
    dispatchDegradedCloseMenu,
    dispatchOutsideClickClose,
    cancelTabRename,
    closeLibraryModal,
} from './escapePolicy';
import { TAB_RENAME_SELECTOR } from './selectors';

/** Fortune renders the sheet-tab rename box `contentEditable` only while
 * actively renaming (react:7081-7083) — matching this class+attribute pair
 * is exactly "Esc landed on an in-progress tab rename". Scoped to `root`
 * (the drawer's own wrapper) so a rename box belonging to a different sheet
 * editor instance is never matched. */
const isTabRenameTarget = (target: EventTarget | null, root: HTMLElement | null): boolean =>
    Boolean(root && target instanceof Element && root.contains(target) && target.matches(TAB_RENAME_SELECTOR));

export interface EscapeHandlerDeps {
    internals: WorkbookInternals | null;
    /** The drawer's own wrapper element — scopes every Esc-policy DOM probe
     *  (toolbar popups, library modals, tab rename) to this sheet editor
     *  instance instead of `document`. */
    root: HTMLElement | null;
    workbookMounted: boolean;
    dialogOpen: boolean;
    isNameBoxTarget: (target: EventTarget | null) => boolean;
    /** True while the KeyTips overlay (Lane K) is armed/open — Esc policy
     *  rule 0 takes over before any of the other rules. */
    keytipsOpen: boolean;
    /** Closes whichever dialog `dialogOpen` refers to. */
    onCloseDialog: () => void;
    /** Closes the drawer (the `close-drawer` outcome). */
    onClose: () => void;
    /** Closes one KeyTips level. Required when `keytipsOpen` can ever be true. */
    onCloseKeytipsLevel?: () => void;
    /** Refocuses the cell input — used after Esc cancels a sheet-tab rename,
     *  so focus doesn't land on `document.body` after the `blur`. */
    focusCellInput: () => void;
}

/**
 * Lane B's Esc outcome executor: builds `EscState`, resolves it via
 * `resolveEscape`, and runs exactly one side effect per outcome. Declared at
 * module scope (not a closure factory) — every input comes through `deps`.
 */
export const handleEscapeEvent = (e: KeyboardEvent, deps: EscapeHandlerDeps): void => {
    const state = buildEscState(deps.internals, {
        workbookMounted: deps.workbookMounted,
        nameBoxTarget: deps.isNameBoxTarget(e.target),
        dialogOpen: deps.dialogOpen,
        keytipsOpen: deps.keytipsOpen,
        tabRenameTarget: isTabRenameTarget(e.target, deps.root),
        root: deps.root,
    });
    const outcome = resolveEscape(state);
    if (outcome === 'pass-through') return;

    e.preventDefault();
    // Plain stopPropagation only blocks a SIBLING bubble-phase
    // listener on this same document node, not a later-registered
    // capture-phase one (e.g. Format Cells' own Esc-to-close effect, mounted
    // after Lane B) — that listener would still see this same keydown and
    // close its dialog independently of the outcome Lane B already
    // committed to, so the drawer could end up closing right behind it.
    // Scoped to the two outcomes that actually close a dialog — every OTHER
    // document-capture Esc listener in the app legitimately needs to keep
    // seeing an Esc Lane B didn't resolve into a dialog close.
    if (outcome === 'close-dialog' || outcome === 'close-library-dialog') e.stopImmediatePropagation();
    else e.stopPropagation();

    switch (outcome) {
        case 'close-dialog':
            deps.onCloseDialog();
            break;
        case 'cancel-rename':
            if (e.target instanceof HTMLElement) cancelTabRename(e.target);
            deps.focusCellInput();
            break;
        case 'close-library-dialog':
            closeLibraryModal(deps.root);
            break;
        case 'close-menu':
            if (deps.internals) deps.internals.setContextWithProduce(closeMenusRecipe);
            else dispatchDegradedCloseMenu();
            // Also closes toolbar popups/"More", which have no context
            // equivalent — only relevant when THIS outcome came from a toolbar
            // popup; context/filter/search menus already closed via the
            // recipe/degraded shim above.
            if (state.toolbarPopupOpen) dispatchOutsideClickClose();
            break;
        case 'close-keytips':
            deps.onCloseKeytipsLevel?.();
            break;
        case 'close-drawer':
            deps.onClose();
            break;
        default: {
            const exhaustiveCheck: never = outcome;
            return exhaustiveCheck;
        }
    }
};
